import { useCallback, useMemo } from 'react';

import { AuthRoleQuery, AuthUserQuery, EntitlementsQuery } from 'dashboard-services/api/auth';
import { CommonService } from 'dashboard-services/services';

import { useInfiniteQuery, useMutation, useQueries, useQuery } from '@tanstack/react-query';

export default (() => {

  const useSearchRoles = ({ query, size = CommonService.getRequestSize(), getQueryBuilder, shouldRefetch = true, shouldBeLimited, includedCategories, excludedCategories, ...rest }) =>
    useInfiniteQuery({
      ...AuthRoleQuery.QUERIES.searchRoles(
        getQueryBuilder,
        {
          query,
          size,
          includedCategories,
          excludedCategories
        }
      ),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      ...rest
    })

    const useUpdateRole = ({ getQueryBuilder, queryClient, id, name, description, onSuccess, ...rest }) =>
      useMutation({
        ...AuthRoleQuery.QUERIES.updateRole(
          getQueryBuilder,
          {
            id,
            name,
            description
          }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.SEARCH() }, { queryKey: AuthRoleQuery.KEYS.GET() })
        },
        ...rest

      })

    const useCreateRole = ({ description, getQueryBuilder, name, queryClient, onSuccess, ...rest }) => 
      useMutation({
        ...AuthRoleQuery.QUERIES.createRole(
          getQueryBuilder,
          {
            name,
            description
          }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.SEARCH() }, { queryKey: AuthRoleQuery.KEYS.GET() })
        },
        ...rest
      })

    const useAssignRolesToUser = ({ getQueryBuilder, queryClient, userName, roleIds, onSuccess, ...rest }) =>
      useMutation({
        ...AuthRoleQuery.QUERIES.assignRolesToUser(
          getQueryBuilder,
          {
            userName,
            roleIds
          }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.GET() }, { queryKey: AuthUserQuery.KEYS.GET_USER() })
        },
        ...rest
      })

    const useAssignUsersToRole = ({ getQueryBuilder, queryClient, roleId, userNames, onSuccess, ...rest }) =>
      useMutation({
        ...AuthRoleQuery.QUERIES.assignUsersToRole(
          getQueryBuilder,
          {
            roleId,
            userNames
          }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.GET() }, { queryKey: AuthUserQuery.KEYS.GET_USER() })
        },
        ...rest
      })

    const useRemoveRolesFromUser = ({ getQueryBuilder, queryClient, userName, roleIds, onSuccess, ...rest }) =>
      useMutation({
        ...AuthRoleQuery.QUERIES.removeRolesFromUser(
          getQueryBuilder,
          {
            userName,
            roleIds
          }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.GET() }, { queryKey: AuthUserQuery.KEYS.GET_USER() })
        },
        ...rest
      })

    const useGetRole = ({ getQueryBuilder, id, ...rest }) =>
      useQuery({
        ...AuthRoleQuery.QUERIES.getRole(
          getQueryBuilder,
          {
            id
          }
        ),

        ...rest
      })

    const useGetRoles = ({ getQueryBuilder, ids }) => {
      const queries = useMemo(() => ids.map(id => ({
              ...AuthRoleQuery.QUERIES.getRole(
                getQueryBuilder,
                {
                  id
                }
              ),
              refetchOnWindowFocus: false
            })), [getQueryBuilder, ids]),
            combine = useCallback(results => ({
              data: results.map(result => result.data?.role).filter(Boolean),
              isPending: results.some(result => result.isPending)
            }), [])
      return useQueries({
        queries,
        combine
      })
    }

    const useRemoveRole = ({ getQueryBuilder, queryClient, id, onSuccess, ...rest }) =>
      useMutation({
        ...AuthRoleQuery.QUERIES.deleteRole(
          getQueryBuilder,
          { id }
        ),
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries({ queryKey: AuthRoleQuery.KEYS.SEARCH() }, { queryKey: AuthRoleQuery.KEYS.GET() })
        },
        ...rest
      })


  const useGetEntitlements = ({ getQueryBuilder, roleId, entitlementType }) =>
    useInfiniteQuery({
      ...EntitlementsQuery.QUERIES.getEntitlementsByRole(
        getQueryBuilder,
        {
          roleId,
          entitlementType,
          size: 1000
        }
      ),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: !!roleId
    })


  const useGetRolesDataAsCsv = ({ getQueryBuilder, ...rest }) =>
    useQuery({
      ...AuthRoleQuery.QUERIES.getCurrentRolesDataAsCsv(getQueryBuilder),
      ...rest
    })

  return {
    useSearchRoles,
    useCreateRole,
    useUpdateRole,
    useAssignRolesToUser,
    useAssignUsersToRole,
    useRemoveRolesFromUser,
    useGetRole,
    useGetRoles,
    useRemoveRole,
    useGetEntitlements,
    useGetRolesDataAsCsv
  }
})()